(() => {
  'use strict';

  if (window.DeebCigarBuyNow) return;

  const ROOT_SELECTOR = '.deeb-cigar-product';
  const BUTTON_SELECTOR = '[data-deeb-cigar-buy-now]';
  const PRIMARY_SUBMIT_SELECTOR = 'product-form [data-deeb-primary-submit]';
  const QUANTITY_SELECTOR = '.quantity__input';
  const VARIANT_SELECTOR = 'form[data-type="add-to-cart-form"] [name="id"]';
  const CART_NOTIFICATION_SELECTOR = 'cart-notification';
  const CART_NOTIFICATION_PANEL_SELECTOR = '#cart-notification';
  const BUY_NOW_ACTIVE_ATTRIBUTE = 'data-deeb-cigar-buy-now-active';
  const CHECKOUT_FALLBACK_MS = 10000;

  class CigarBuyNowController {
    constructor(root) {
      this.root = root;
      this.button = root.querySelector(BUTTON_SELECTOR);
      this.formId = String(this.button?.dataset.productFormId || '').trim();
      this.form = this.formId ? document.getElementById(this.formId) : null;
      this.productForm = this.form?.closest('product-form') || null;
      this.primarySubmit = root.querySelector(PRIMARY_SUBMIT_SELECTOR);
      this.quantityInput = root.querySelector(QUANTITY_SELECTOR);
      this.variantInput =
        this.form?.querySelector('[name="id"]') ||
        root.querySelector(VARIANT_SELECTOR);
      this.spinner = this.button?.querySelector('.loading__spinner') || null;
      this.label =
        this.button?.querySelector('[data-deeb-cigar-buy-now-label]') || null;
      this.defaultLabel = String(
        this.button?.dataset.defaultLabel || this.label?.textContent || ''
      ).trim();
      this.loadingLabel = String(this.button?.dataset.loadingLabel || '').trim();
      this.messages = Object.freeze({
        invalidQuantity: String(
          this.button?.dataset.invalidQuantityMessage || ''
        ).trim(),
        unavailable: String(this.button?.dataset.unavailableMessage || '').trim(),
        checkoutUnavailable: String(
          this.button?.dataset.checkoutUnavailableMessage || ''
        ).trim(),
        checkoutFailed: String(
          this.button?.dataset.checkoutFailedMessage || ''
        ).trim(),
      });

      this.isBusy = false;
      this.isRedirecting = false;
      this.submitRequested = false;
      this.pendingVariantId = null;
      this.primaryLockApplied = false;
      this.checkoutFallbackTimeout = null;
      this.destroyed = false;
      this.unsubscribers = [];
      this.cartNotification = document.querySelector(
        CART_NOTIFICATION_SELECTOR
      );
      this.cartNotificationPanel =
        this.cartNotification?.querySelector(CART_NOTIFICATION_PANEL_SELECTOR) ||
        document.querySelector(CART_NOTIFICATION_PANEL_SELECTOR);
      this.cartNotificationObserver = null;
      this.cartNotificationSuppressed = false;

      if (!this.ready) return;

      this.button.addEventListener('click', this.handleClick);
      this.root.addEventListener('input', this.handleStateEvent, true);
      this.root.addEventListener('change', this.handleStateEvent, true);

      this.submitObserver = new MutationObserver(this.handlePrimaryMutation);
      this.submitObserver.observe(this.primarySubmit, {
        attributes: true,
        attributeFilter: [
          'class',
          'disabled',
          'aria-disabled',
          'data-deeb-submit-state',
        ],
      });

      this.subscribeToCommerceLifecycle();
      this.syncState();
    }

    get ready() {
      return Boolean(
        this.button &&
          this.form &&
          this.productForm &&
          this.primarySubmit &&
          this.quantityInput &&
          this.variantInput &&
          this.defaultLabel &&
          this.loadingLabel &&
          this.messages.invalidQuantity &&
          this.messages.unavailable &&
          this.messages.checkoutUnavailable &&
          this.messages.checkoutFailed
      );
    }

    get quantityIsValid() {
      const value = Number(this.quantityInput.value);
      if (!Number.isFinite(value) || value <= 0) return false;

      if (
        this.quantityInput.type !== 'hidden' &&
        !this.quantityInput.checkValidity()
      ) {
        return false;
      }

      const min = Number(
        this.quantityInput.dataset.min || this.quantityInput.min || 1
      );
      const maxRaw =
        this.quantityInput.dataset.max || this.quantityInput.max || '';
      const max = maxRaw === '' ? null : Number(maxRaw);

      if (Number.isFinite(min) && value < min) return false;
      if (max !== null && Number.isFinite(max) && value > max) return false;

      return true;
    }

    get variantIsAvailable() {
      return Boolean(this.variantInput.value) && !this.variantInput.disabled;
    }

    get primarySubmitIsAvailable() {
      return (
        !this.primarySubmit.disabled &&
        this.primarySubmit.getAttribute('aria-disabled') !== 'true'
      );
    }

    get canUseDawnTransport() {
      return (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      );
    }

    subscribeToCommerceLifecycle() {
      if (!this.canUseDawnTransport) return;

      this.unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate),
        subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError),
        subscribe(PUB_SUB_EVENTS.quantityUpdate, this.scheduleSync),
        subscribe(PUB_SUB_EVENTS.variantChange, this.scheduleSync)
      );
    }

    handleStateEvent = (event) => {
      if (
        event.target !== this.quantityInput &&
        event.target !== this.variantInput
      ) {
        return;
      }

      this.syncState();
    };

    scheduleSync = () => {
      queueMicrotask(() => this.syncState());
    };

    syncState = () => {
      if (!this.ready || this.destroyed) return;

      const disabled =
        this.isBusy ||
        !this.quantityIsValid ||
        !this.variantIsAvailable ||
        !this.primarySubmitIsAvailable;

      this.button.disabled = disabled;
      this.button.setAttribute('aria-disabled', String(disabled));
    };

    showError(message) {
      this.productForm.handleErrorMessage?.(message);
    }

    validateSelection() {
      if (!this.quantityIsValid) {
        const message =
          this.quantityInput.validationMessage ||
          this.messages.invalidQuantity;

        this.quantityInput.setAttribute('aria-invalid', 'true');
        this.showError(message);

        if (this.quantityInput.type !== 'hidden') {
          this.quantityInput.focus();
        }

        return false;
      }

      if (!this.variantIsAvailable || !this.primarySubmitIsAvailable) {
        this.showError(this.messages.unavailable);
        return false;
      }

      if (!this.canUseDawnTransport) {
        this.showError(this.messages.checkoutUnavailable);
        return false;
      }

      this.quantityInput.removeAttribute('aria-invalid');
      this.showError();
      return true;
    }

    setBusy(isBusy) {
      this.isBusy = isBusy;
      this.button.classList.toggle('loading', isBusy);
      this.button.toggleAttribute('aria-busy', isBusy);
      this.spinner?.classList.toggle('hidden', !isBusy);

      if (this.label) {
        this.label.textContent = isBusy
          ? this.loadingLabel
          : this.defaultLabel;
      }

      this.syncState();
    }

    lockPrimarySubmit() {
      if (this.primarySubmit.disabled) return;

      this.primarySubmit.disabled = true;
      this.primaryLockApplied = true;
    }

    unlockPrimarySubmit() {
      if (!this.primaryLockApplied) return;

      this.primarySubmit.removeAttribute('disabled');
      this.primaryLockApplied = false;
    }

    suppressCartNotification() {
      if (this.cartNotificationSuppressed) return;

      this.cartNotificationSuppressed = true;
      document.documentElement.setAttribute(BUY_NOW_ACTIVE_ATTRIBUTE, '');
      this.cartNotificationPanel?.setAttribute('inert', '');
      this.closeCartNotification();

      if (!this.cartNotificationPanel) return;

      this.cartNotificationObserver = new MutationObserver(
        this.handleCartNotificationMutation
      );
      this.cartNotificationObserver.observe(this.cartNotificationPanel, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden'],
      });
    }

    handleCartNotificationMutation = () => {
      if (!this.cartNotificationSuppressed) return;
      this.closeCartNotification();
    };

    closeCartNotification() {
      const panel = this.cartNotificationPanel;
      const notificationIsOpen =
        panel?.classList.contains('active') ||
        panel?.getAttribute('aria-hidden') === 'false';

      if (!notificationIsOpen) return;
      this.cartNotification?.close?.();
    }

    releaseCartNotificationSuppression({ close = true } = {}) {
      if (close) this.closeCartNotification();

      this.cartNotificationObserver?.disconnect();
      this.cartNotificationObserver = null;
      this.cartNotificationPanel?.removeAttribute('inert');
      document.documentElement.removeAttribute(BUY_NOW_ACTIVE_ATTRIBUTE);
      this.cartNotificationSuppressed = false;
    }

    clearTimers() {
      window.clearTimeout(this.checkoutFallbackTimeout);
      this.checkoutFallbackTimeout = null;
    }

    resetLifecycle({ clearError = false } = {}) {
      this.clearTimers();
      this.releaseCartNotificationSuppression();
      this.unlockPrimarySubmit();
      this.isRedirecting = false;
      this.submitRequested = false;
      this.pendingVariantId = null;
      this.setBusy(false);

      if (clearError) this.showError();
    }

    matchesPendingVariant(productVariantId) {
      return Boolean(
        this.isBusy &&
          this.pendingVariantId &&
          String(productVariantId || '') === this.pendingVariantId
      );
    }

    handleCartUpdate = ({ source, productVariantId } = {}) => {
      if (
        source !== 'product-form' ||
        !this.matchesPendingVariant(productVariantId)
      ) {
        return;
      }

      this.isRedirecting = true;

      const checkoutUrl = `${window.Shopify?.routes?.root || '/'}checkout`;
      window.location.assign(checkoutUrl);

      this.checkoutFallbackTimeout = window.setTimeout(() => {
        if (!this.root.isConnected) return;
        this.resetLifecycle();
      }, CHECKOUT_FALLBACK_MS);
    };

    handleCartError = ({ source, productVariantId } = {}) => {
      if (
        source !== 'product-form' ||
        !this.matchesPendingVariant(productVariantId)
      ) {
        return;
      }

      queueMicrotask(() => this.resetLifecycle());
    };

    handlePrimaryMutation = () => {
      this.syncState();

      if (!this.isBusy || !this.submitRequested || this.isRedirecting) return;
      if (this.primarySubmit.classList.contains('loading')) return;

      queueMicrotask(() => {
        if (!this.isBusy || this.isRedirecting) return;
        if (this.primarySubmit.classList.contains('loading')) return;

        this.showError(this.messages.checkoutFailed);
        this.resetLifecycle();
      });
    };

    handlePageShow() {
      if (!this.isBusy && !this.isRedirecting) return;
      this.resetLifecycle();
    }

    handleClick = (event) => {
      event.preventDefault();

      if (this.isBusy || !this.validateSelection()) return;

      this.pendingVariantId = String(this.variantInput.value || '');
      this.submitRequested = true;
      this.setBusy(true);
      this.suppressCartNotification();

      try {
        this.form.requestSubmit(this.primarySubmit);
      } catch (error) {
        console.error(error);
        this.showError(this.messages.checkoutFailed);
        this.resetLifecycle();
        return;
      }

      const dawnRequestStarted =
        this.primarySubmit.classList.contains('loading') ||
        this.primarySubmit.getAttribute('aria-disabled') === 'true';

      if (!dawnRequestStarted) {
        this.showError(this.messages.checkoutFailed);
        this.resetLifecycle();
        return;
      }

      this.lockPrimarySubmit();
    };

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      this.button?.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('input', this.handleStateEvent, true);
      this.root.removeEventListener('change', this.handleStateEvent, true);
      this.submitObserver?.disconnect();

      this.unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      this.unsubscribers = [];

      this.clearTimers();
      this.releaseCartNotificationSuppression();
      this.unlockPrimarySubmit();
    }
  }

  const instances = new Map();

  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) {
      return null;
    }

    const existing = instances.get(root);
    if (existing) return existing;

    const instance = new CigarBuyNowController(root);
    if (!instance.ready) return null;

    instances.set(root, instance);
    return instance;
  };

  const initializeAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) {
      initializeRoot(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  const destroyWithin = (scope) => {
    instances.forEach((instance, root) => {
      if (
        root === scope ||
        scope?.contains?.(root) ||
        !root.isConnected
      ) {
        instance.destroy();
        instances.delete(root);
      }
    });
  };

  const syncAll = () => {
    queueMicrotask(() => {
      destroyWithin(null);
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        initializeRoot(root)?.syncState();
      });
    });
  };

  const handleProductInfoLoaded = (event) => {
    destroyWithin(null);
    initializeRoot(event.target);
  };

  const handleSectionLoad = (event) => {
    destroyWithin(null);
    initializeAll(event.target);
  };

  const handleSectionUnload = (event) => {
    destroyWithin(event.target);
  };

  const handlePageShow = () => {
    instances.forEach((instance) => instance.handlePageShow());
    syncAll();
  };

  document.addEventListener('product-info:loaded', handleProductInfoLoaded);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  window.addEventListener('pageshow', handlePageShow);

  initializeAll();

  window.DeebCigarBuyNow = Object.freeze({
    sync: syncAll,
    destroy: () => destroyWithin(document),
  });
})();
